/**
 * Canonical money helpers, shared across every feature that renders Stripe
 * amounts (catalog, subscriptions, finance/payouts, ...).
 *
 * Stripe stores amounts in the currency's smallest unit ("minor units":
 * cents for USD, yen for JPY). Zero-decimal currencies have no minor unit,
 * so their "minor" value already IS the major amount and must NOT be divided
 * by 100. The zero-decimal list is Stripe's:
 * https://docs.stripe.com/currencies#zero-decimal
 *
 * Home is `shared/lib/` because three features need it — the cross-feature
 * import rule (CLAUDE.md) forbids one feature reaching into another's copy.
 * `catalog/format-money.ts` and `subscriptions/format-amount.ts` delegate
 * here so the zero-decimal table lives in exactly one place.
 *
 * Side-effect free — unit-tested in `money.test.ts`.
 */
const ZERO_DECIMAL_CURRENCIES: ReadonlySet<string> = new Set([
  "bif",
  "clp",
  "djf",
  "gnf",
  "jpy",
  "kmf",
  "krw",
  "mga",
  "pyg",
  "rwf",
  "ugx",
  "vnd",
  "vuv",
  "xaf",
  "xof",
  "xpf",
]);

export function isZeroDecimalCurrency(currency: string): boolean {
  return ZERO_DECIMAL_CURRENCIES.has(currency.toLowerCase());
}

/**
 * Minor units (e.g. cents) → locale-formatted currency string.
 * `null` / `NaN` → em-dash so one unparseable value never breaks a layout.
 * Unknown currency codes fall back to `<amount> <CODE>` rather than throwing.
 */
export function formatMinorUnits(amountMinor: number | null, currency: string): string {
  if (amountMinor === null || Number.isNaN(amountMinor)) return "—";

  const code = currency.toLowerCase();
  const isZeroDecimal = ZERO_DECIMAL_CURRENCIES.has(code);
  const major = isZeroDecimal ? amountMinor : amountMinor / 100;

  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code.toUpperCase(),
      minimumFractionDigits: isZeroDecimal ? 0 : 2,
      maximumFractionDigits: isZeroDecimal ? 0 : 2,
    }).format(major);
  } catch {
    return `${major} ${code.toUpperCase()}`;
  }
}

/**
 * Major-unit NUMBER (e.g. `12.5` dollars, `1200` yen) → integer minor units.
 * Rounds to avoid float drift (`19.99 * 100 = 1998.9999…`). Non-finite → null.
 * Zero-decimal currencies are already in their smallest unit — no ×100.
 */
export function majorNumberToMinorUnits(amount: number, currency: string): number | null {
  if (!Number.isFinite(amount)) return null;
  if (isZeroDecimalCurrency(currency)) return Math.round(amount);
  return Math.round(amount * 100);
}

/**
 * Major-unit STRING (user-typed, e.g. `"29.99"`) → integer minor units.
 * Strict: empty/negative/non-numeric → null; more than two fractional digits
 * → null (never silently round `29.999` to `$30.00`); zero-decimal currencies
 * reject any fractional part.
 */
export function majorStringToMinorUnits(input: string, currency: string): number | null {
  const trimmed = input.trim();
  if (trimmed.length === 0) return null;
  const parsed = Number.parseFloat(trimmed);
  if (!Number.isFinite(parsed)) return null;
  if (parsed < 0) return null;

  if (isZeroDecimalCurrency(currency)) {
    if (!Number.isInteger(parsed)) return null;
    return parsed;
  }

  const decimalIdx = trimmed.indexOf(".");
  if (decimalIdx !== -1 && trimmed.length - decimalIdx - 1 > 2) return null;

  return Math.round(parsed * 100);
}
